import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateLessonMutation } from '../api/courseApi';
import { assignLessonTag, tagKeys } from '../api/tagApi';
import { getRememberedListPath } from '../hooks/useListPathMemory';
import { LessonMetadataPane } from '../components/LessonMetadataPane';

export function CreateLessonPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draftLessonMeta, setDraftLessonMeta] = useState({
    title: '',
    description: '',
    estimatedTimeMinutes: undefined as number | undefined,
  });
  const [pendingTagIds, setPendingTagIds] = useState<number[]>([]);
  const [createLessonError, setCreateLessonError] = useState<string | null>(null);
  // Covers the gap between the lesson POST resolving (which flips isCreating
  // off) and navigation away — keeps the form from re-enabling mid-flight
  // while tags for the just-created lesson are still being assigned.
  const [isFinalizing, setIsFinalizing] = useState(false);

  const { mutate: createLesson, isPending: isCreating } = useCreateLessonMutation({
    onSuccess: async (newLesson) => {
      if (pendingTagIds.length > 0) {
        setIsFinalizing(true);
        // Assigned one request per tag rather than blocked on a batch
        // endpoint — a single failed tag shouldn't stop the others or stop
        // the user from landing on their newly-created lesson.
        await Promise.allSettled(pendingTagIds.map((tagId) => assignLessonTag(newLesson.id, tagId)));
        queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      }

      const hasDescription = Boolean(draftLessonMeta.description.trim());
      const hasEstimatedTime = draftLessonMeta.estimatedTimeMinutes !== undefined;
      const pendingLessonMeta = hasDescription || hasEstimatedTime
        ? {
            ...(hasDescription ? { description: draftLessonMeta.description } : {}),
            ...(hasEstimatedTime ? { estimatedTimeMinutes: draftLessonMeta.estimatedTimeMinutes } : {}),
          }
        : undefined;

      if (pendingLessonMeta) {
        navigate(`/lessons/${newLesson.id}`, { state: { pendingLessonMeta } });
      } else {
        navigate(`/lessons/${newLesson.id}`);
      }
    },
    onError: (error) => {
      setCreateLessonError(error.message || 'Failed to create lesson');
    },
  });

  const handleTitleChange = useCallback((title: string) => {
    setDraftLessonMeta((current) => ({ ...current, title }));
    setCreateLessonError(null);
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setDraftLessonMeta((current) => ({ ...current, description }));
    setCreateLessonError(null);
  }, []);

  const handleEstimatedTimeMinutesChange = useCallback((estimatedTimeMinutes: number | undefined) => {
    setDraftLessonMeta((current) => ({ ...current, estimatedTimeMinutes }));
    setCreateLessonError(null);
  }, []);

  const handleCreateLesson = useCallback(() => {
    if (isCreating || isFinalizing) return;

    createLesson({
      title: draftLessonMeta.title,
      description: draftLessonMeta.description,
    });
  }, [createLesson, draftLessonMeta.description, draftLessonMeta.title, isCreating, isFinalizing]);

  const handleCancel = useCallback(() => {
    navigate(getRememberedListPath('/lessons'));
  }, [navigate]);

  return (
    <LessonMetadataPane
      lessonMeta={{
        id: '',
        title: draftLessonMeta.title,
        description: draftLessonMeta.description,
        estimatedTimeMinutes: draftLessonMeta.estimatedTimeMinutes,
        workflowState: 'draft',
        createdAt: '',
        updatedAt: '',
      }}
      mode="create"
      isSubmitting={isCreating || isFinalizing}
      error={createLessonError}
      pendingTagIds={pendingTagIds}
      onTitleChange={handleTitleChange}
      onDescriptionChange={handleDescriptionChange}
      onEstimatedTimeMinutesChange={handleEstimatedTimeMinutesChange}
      onPendingTagIdsChange={setPendingTagIds}
      onSubmit={handleCreateLesson}
      onCancel={handleCancel}
    />
  );
}
